import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from '../store'
import { motion } from 'framer-motion'
import { 
  BuildingOffice2Icon, 
  UserIcon, 
  TrophyIcon,
  BookOpenIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import { updateProfileSuccess } from '../store/slices/authSlice'
import toast from 'react-hot-toast'

interface SetupFormData {
  school: string
  grade: string
  targetExam: string
  studyGoal: string
  dailyStudyHours: number
  favoriteSubjects: string[]
  weakSubjects: string[]
  studyStyle: string
}

const Setup: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useSelector((state: RootState) => state.auth)
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState<SetupFormData>({
    school: '',
    grade: '',
    targetExam: 'LGS',
    studyGoal: '',
    dailyStudyHours: 2,
    favoriteSubjects: [],
    weakSubjects: [],
    studyStyle: 'visual'
  })

  const totalSteps = 4

  const grades = ['5. Sınıf', '6. Sınıf', '7. Sınıf', '8. Sınıf']
  const subjects = [
    'Matematik', 'Türkçe', 'Fen Bilimleri', 'Sosyal Bilgiler', 
    'İngilizce', 'Din Kültürü', 'Resim', 'Müzik', 'Beden Eğitimi'
  ]
  const studyStyles = [
    { id: 'visual', name: 'Görsel Öğrenme', icon: '👁️', desc: 'Şemalar, grafikler ve resimlerle öğrenmeyi seviyorum' },
    { id: 'auditory', name: 'İşitsel Öğrenme', icon: '👂', desc: 'Dinleyerek ve konuşarak öğrenmeyi seviyorum' },
    { id: 'kinesthetic', name: 'Kinestetik Öğrenme', icon: '✋', desc: 'Yaparak ve deneyerek öğrenmeyi seviyorum' },
    { id: 'reading', name: 'Okuma/Yazma', icon: '📚', desc: 'Okuyarak ve yazarak öğrenmeyi seviyorum' }
  ]

  const handleSubjectToggle = (subject: string, type: 'favorite' | 'weak') => {
    const field = type === 'favorite' ? 'favoriteSubjects' : 'weakSubjects'
    const currentList = formData[field]
    
    if (currentList.includes(subject)) {
      setFormData(prev => ({
        ...prev,
        [field]: currentList.filter(s => s !== subject)
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: [...currentList, subject]
      }))
    }
  }

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleComplete = () => {
    if (!user) {
      toast.error('Kullanıcı bilgisi bulunamadı')
      navigate('/login')
      return
    }

    // Mevcut kullanıcı verilerini setup bilgileri ile güncelle
    const updatedUserData = {
      school: formData.school,
      grade: formData.grade,
      targetExam: formData.targetExam,
      studyGoal: formData.studyGoal,
      dailyStudyHours: formData.dailyStudyHours,
      favoriteSubjects: formData.favoriteSubjects,
      weakSubjects: formData.weakSubjects,
      studyStyle: formData.studyStyle,
      profileCompleted: true,
      joinDate: user.createdAt, // Mevcut kayıt tarihini koru
      // Yeni rozet ekle
      badges: [...(user.badges || []), 'Profil Tamamlandı'],
      // Avatar yoksa oluştur
      avatar: user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`,
      // Stats'ı güncelle
      stats: {
        ...user.stats,
        achievements: (user.stats?.achievements || 0) + 1
      }
    }

    dispatch(updateProfileSuccess(updatedUserData))
    toast.success('Profil kurulumu tamamlandı! 🎉')
    navigate('/dashboard')
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BuildingOffice2Icon className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Okul Bilgilerin</h2>
              <p className="text-gray-600">Hangi okulda ve sınıfta okuyorsun?</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Okul Adı
                </label>
                <input
                  type="text"
                  value={formData.school}
                  onChange={(e) => setFormData(prev => ({ ...prev, school: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Örn: Atatürk Ortaokulu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sınıf
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {grades.map((grade) => (
                    <button
                      key={grade}
                      onClick={() => setFormData(prev => ({ ...prev, grade }))}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.grade === grade
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {grade}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrophyIcon className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Hedeflerin</h2>
              <p className="text-gray-600">Çalışma hedeflerini belirleyelim</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hedef Sınav
                </label>
                <select
                  value={formData.targetExam}
                  onChange={(e) => setFormData(prev => ({ ...prev, targetExam: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="LGS">LGS (Liselere Geçiş Sınavı)</option>
                  <option value="Okul Sınavları">Okul Sınavları</option>
                  <option value="Genel Başarı">Genel Başarı</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Çalışma Hedefin
                </label>
                <textarea
                  value={formData.studyGoal}
                  onChange={(e) => setFormData(prev => ({ ...prev, studyGoal: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Örn: LGS'de yüksek puan alarak iyi bir liseye gitmek istiyorum"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Günlük Çalışma Saati: {formData.dailyStudyHours} saat
                </label>
                <input
                  type="range"
                  min="1"
                  max="8"
                  value={formData.dailyStudyHours}
                  onChange={(e) => setFormData(prev => ({ ...prev, dailyStudyHours: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>1 saat</span>
                  <span>8 saat</span>
                </div>
              </div>
            </div>
          </motion.div>
        )

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpenIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Ders Tercihlerin</h2>
              <p className="text-gray-600">Hangi dersleri seviyorsun ve hangilerinde zorlanıyorsun?</p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Sevdiğin Dersler (En fazla 3 tane seç)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {subjects.map((subject) => (
                    <button
                      key={subject}
                      onClick={() => handleSubjectToggle(subject, 'favorite')}
                      disabled={!formData.favoriteSubjects.includes(subject) && formData.favoriteSubjects.length >= 3}
                      className={`p-3 rounded-lg border-2 transition-all text-sm ${
                        formData.favoriteSubjects.includes(subject)
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                    >
                      {formData.favoriteSubjects.includes(subject) && (
                        <CheckIcon className="w-4 h-4 inline mr-1" />
                      )}
                      {subject}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Zorlandığın Dersler (En fazla 3 tane seç)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {subjects.map((subject) => (
                    <button
                      key={subject}
                      onClick={() => handleSubjectToggle(subject, 'weak')}
                      disabled={!formData.weakSubjects.includes(subject) && formData.weakSubjects.length >= 3}
                      className={`p-3 rounded-lg border-2 transition-all text-sm ${
                        formData.weakSubjects.includes(subject)
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                    >
                      {formData.weakSubjects.includes(subject) && (
                        <CheckIcon className="w-4 h-4 inline mr-1" />
                      )}
                      {subject}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Öğrenme Stilin</h2>
              <p className="text-gray-600">Nasıl öğrenmeyi tercih ediyorsun?</p>
            </div>

            <div className="space-y-3">
              {studyStyles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setFormData(prev => ({ ...prev, studyStyle: style.id }))}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                    formData.studyStyle === style.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">{style.icon}</span>
                    <div>
                      <h3 className="font-medium text-gray-900">{style.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{style.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )

      default:
        return null
    }
  }

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.school.trim() !== '' && formData.grade !== ''
      case 2:
        return formData.studyGoal.trim() !== ''
      case 3:
        return formData.favoriteSubjects.length > 0 || formData.weakSubjects.length > 0
      case 4:
        return formData.studyStyle !== ''
      default:
        return false
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Adım {currentStep} / {totalSteps}
            </span>
            <span className="text-sm font-medium text-gray-600">
              %{Math.round((currentStep / totalSteps) * 100)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {renderStep()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handlePrev}
              disabled={currentStep === 1}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Geri
            </button>

            {currentStep < totalSteps ? (
              <button
                onClick={handleNext}
                disabled={!isStepValid()}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
              >
                İleri
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={!isStepValid()}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg hover:from-green-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
              >
                Tamamla 🎉
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Setup